import type { Language } from './types';

/**
 * Uzbek dates cannot go through `Intl`: the engines disagree. For `uz-UZ` Chromium has no
 * month names at all and prints «M01 16» … «M12 16», while WebKit prints «16-yan» … «16-dek»
 * — the same code shows a different string to half the audience, and one of the two is
 * unreadable. Measured in both engines, all twelve months. So the Uzbek abbreviations are
 * ours, spelled the way WebKit (CLDR) spells them, and only ru/en go through `Intl`, where
 * the two engines agree byte for byte.
 */
const UZ_SHORT_MONTHS = ['yan', 'fev', 'mar', 'apr', 'may', 'iyn', 'iyl', 'avg', 'sen', 'okt', 'noy', 'dek'];

/**
 * Persian is the same trap one step deeper. `fa-IR` through `Intl` switches calendar as well
 * as script: the default numbering system is `arabext` (۱۶) and the default calendar Jalali,
 * so 16 August prints «۲۵ مرداد» — a different day number for a date the rest of the app
 * shows in Gregorian, next to Latin-digit prices and timers. So the Gregorian month names in
 * Persian are ours, and the clock goes through `en-GB`, whose 24-hour Latin-digit output both
 * engines render identically.
 */
const FA_MONTHS = [
  'ژانویه', 'فوریه', 'مارس', 'آوریل', 'مه', 'ژوئن',
  'ژوئیه', 'اوت', 'سپتامبر', 'اکتبر', 'نوامبر', 'دسامبر',
];

const DATE_LOCALE: Record<Language, string> = { ru: 'ru-RU', en: 'en-US', uz: 'uz-UZ', fa: 'en-GB' };

/** Persian separates a date from the clock with its own comma. */
const TIME_SEPARATOR: Record<Language, string> = { ru: ', ', en: ', ', uz: ', ', fa: '، ' };

/**
 * Day and short month in the reader's language: «16 авг.», «Aug 16», «16-avg», «16 اوت».
 *
 * `utc` is for calendar dates (`YYYY-MM-DD`), which parse as UTC midnight: read in the
 * viewer's zone they slip a day back for everyone west of UTC.
 */
export function formatDayMonth(date: Date | string, lang: Language, { utc = false } = {}): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return '';
  if (lang === 'uz') {
    const day = utc ? d.getUTCDate() : d.getDate();
    return `${day}-${UZ_SHORT_MONTHS[utc ? d.getUTCMonth() : d.getMonth()]}`;
  }
  if (lang === 'fa') {
    const day = utc ? d.getUTCDate() : d.getDate();
    return `${day} ${FA_MONTHS[utc ? d.getUTCMonth() : d.getMonth()]}`;
  }
  return d.toLocaleDateString(DATE_LOCALE[lang], {
    day: 'numeric',
    month: 'short',
    timeZone: utc ? 'UTC' : undefined,
  });
}

/**
 * The same date with the clock: «16 авг., 13:20», «Aug 16, 01:20 PM», «16-avg, 13:20».
 * The time half goes through `Intl` for every language — hours and minutes are the one part
 * both engines render identically, including Uzbek — so only the date half needs us.
 */
export function formatDayMonthTime(date: Date | string, lang: Language): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return '';
  const time = d.toLocaleTimeString(DATE_LOCALE[lang], { hour: '2-digit', minute: '2-digit' });
  return `${formatDayMonth(d, lang)}${TIME_SEPARATOR[lang]}${time}`;
}
